import { useState, useMemo } from "react";
import { TrendingUp, Users, Download } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, ExportButton, EmptyState } from "@/components/admin/shared";
import { useCLV } from "@/hooks/admin/useCRM";
import { formatFCFA } from "@cieda/utils";
import type { LoyaltyTier, CLVListParams } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Tier config
// ---------------------------------------------------------------------------

const TIER_LABELS: Record<LoyaltyTier, string> = {
  bronze: "Bronze",
  silver: "Argent",
  gold: "Or",
};

const TIER_STYLES: Record<LoyaltyTier, string> = {
  bronze: "bg-amber-50 text-amber-700",
  silver: "bg-stone-100 text-stone-600",
  gold: "bg-yellow-50 text-yellow-700",
};

// ---------------------------------------------------------------------------
// Sort options
// ---------------------------------------------------------------------------

type SortOption = NonNullable<CLVListParams["sort"]>;

const SORT_OPTIONS: Array<{ value: SortOption; label: string }> = [
  { value: "historical_desc", label: "CLV historique (↓)" },
  { value: "predicted_desc", label: "CLV prédit (↓)" },
  { value: "basket_desc", label: "Panier moyen (↓)" },
];

// ---------------------------------------------------------------------------
// Export columns
// ---------------------------------------------------------------------------

const EXPORT_COLUMNS = [
  { key: "full_name", label: "Client" },
  { key: "tier", label: "Tier" },
  { key: "historical_clv_fcfa", label: "CLV historique (FCFA)" },
  { key: "predicted_clv_12m_fcfa", label: "CLV prédit 12 mois (FCFA)" },
  { key: "avg_basket_fcfa", label: "Panier moyen (FCFA)" },
  { key: "order_frequency_per_month", label: "Fréquence/mois" },
] as const;

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CLV() {
  const [sort, setSort] = useState<SortOption>("historical_desc");
  const [page, setPage] = useState(1);

  const params: CLVListParams = useMemo(
    () => ({ page, limit: 20, sort }),
    [page, sort],
  );

  const { data, isLoading } = useCLV(params);

  const customers = data?.customers ?? [];
  const pagination = data?.pagination;

  const exportData = useMemo(
    () =>
      customers.map((c) => ({
        full_name: c.full_name,
        tier: TIER_LABELS[c.tier],
        historical_clv_fcfa: c.historical_clv_fcfa,
        predicted_clv_12m_fcfa: c.predicted_clv_12m_fcfa,
        avg_basket_fcfa: c.avg_basket_fcfa,
        order_frequency_per_month: (c.order_frequency_per_month ?? 0).toFixed(1),
      })),
    [customers],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Valeur vie client (CLV)
              </h1>
              <p className="text-sm text-stone-500">
                Analyse de la valeur et des prédictions de revenus
              </p>
            </div>
          </div>
          <ExportButton
            data={exportData}
            columns={[...EXPORT_COLUMNS]}
            filename="clv-clients"
          />
        </div>

        {/* KPI cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 animate-pulse">
            {[...Array(3)].map((_, i) => <div key={i} className="h-28 rounded-lg bg-stone-100" />)}
          </div>
        ) : data ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KPICard
              label="CLV moyen"
              value={data.avg_clv_fcfa}
              format="currency"
              icon={<TrendingUp className="h-4 w-4" />}
            />
            <KPICard
              label="CLV top 10%"
              value={data.top10_avg_clv_fcfa}
              format="currency"
              icon={<Users className="h-4 w-4" />}
            />
            <KPICard
              label="Revenu top 10% (Pareto)"
              value={data.top10_revenue_share_percent}
              format="percent"
            />
          </div>
        ) : null}

        {/* Sort + download */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-stone-500">Trier par :</label>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value as SortOption); setPage(1); }}
              className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {isLoading ? (
            <div className="space-y-3 p-4 animate-pulse">
              {[...Array(8)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
            </div>
          ) : customers.length === 0 ? (
            <EmptyState
              icon={<Download className="h-6 w-6" />}
              title="Aucune donnée CLV disponible"
              description="Les données de valeur client sont calculées après suffisamment de commandes."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Client</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Tier</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">CLV historique</th>
                    <th className="hidden md:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">CLV prédit 12 mois</th>
                    <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Panier moyen</th>
                    <th className="hidden xl:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Fréquence/mois</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {customers.map((c) => (
                    <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-medium text-emerald-800">
                            {c.full_name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{c.full_name}</p>
                            <p className="text-xs text-stone-400">{c.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_STYLES[c.tier]}`}>
                          {TIER_LABELS[c.tier]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-stone-800">
                        {formatFCFA(c.historical_clv_fcfa)}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                        {formatFCFA(c.predicted_clv_12m_fcfa)}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {formatFCFA(c.avg_basket_fcfa)}
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {(c.order_frequency_per_month ?? 0).toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total > pagination.limit && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              {((pagination.page - 1) * pagination.limit) + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
